/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, scan, switchMap, tap } from 'rxjs/operators';
import { pokeProjectPresence } from '@touchplan/firebase-common';
import { AppState } from '../app-state.service';
import { RouteUtils } from '../route-utils.service';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { fancyDatabase } from '@touchplan/client-firebase';

@Injectable({
	providedIn: 'root',
})
export class GlobalPresenceMonitor {
	private readonly MAX_POKE_PERIOD_MINUTES = 30;
	// private IDLE_TIMEOUT = 1000 * 60 * 60; // 1000ms * 60 sec * 60 min = 1 hour
	private IDLE_TIMEOUT = 1000 * 60 * 60 * 168; // 1000ms * 60 sec * 60 min * 168 hours = 168 hour
	private logger: ILoggerService;
	private currentPresencesProjectId: string;
	private currentPresencesUserId: string;
	private currentPresencesTimeInMS: number;

	private userIsActiveSubject = new BehaviorSubject<boolean>(false);

	private isUserInactivatedDueToIdle$ = combineLatest([
		this.userIsActiveSubject.asObservable(),
		this.appState.user.pipe(distinctUntilChanged((a, b) => a?.$id === b?.$id)),
	]).pipe(
		filter(([isActive, user]) => isActive && !!user),
		map(() => [new Date(), false]),
		scan(([lastTimeActiveDate, _lastTimeIsInactive], [currDate, _currIsInactive]) => {
			const isInactivated =
				this.timeElapsedMilliseconds(lastTimeActiveDate as Date, currDate as Date) > this.IDLE_TIMEOUT;
			return [new Date(), isInactivated];
		}),
		map(([_, isInactivated]) => isInactivated as boolean),
		tap((isInactivated) => {
			if (isInactivated) {
				this.logger.log('User inactivated due to inactivity');
			}
		})
	);

	public idleLogout$(idleEnabled$: Observable<boolean>): Observable<boolean> {
		return idleEnabled$.pipe(
			switchMap((isEnabled) =>
				isEnabled ? this.isUserInactivatedDueToIdle$.pipe(filter((isInactivated) => isInactivated)) : EMPTY
			)
		);
	}

	constructor(private routeUtils: RouteUtils, private appState: AppState) {
		this.logger = LoggerServiceFactory.create('GlobalPresenceMonitor');
	}

	public start() {
		this.startUserActivityListeners();

		const projectId$ = this.routeUtils.allRouteParam$.pipe(
			pairwise(),
			// filter(pair => !deepEquals(pair[0].params, pair[1].params))
			map((pair) => pair[1].paramMap.get('projectId'))
		);

		combineLatest([
			projectId$,
			this.appState.user.pipe(distinctUntilChanged((a, b) => a?.$id === b?.$id)),
			this.userIsActiveSubject,
		])
			.pipe(filter(([projectId]) => !!projectId))
			.subscribe(([projectId, user, userIsActive]) => {
				const userId = user.$id;
				const timeNowInMS = new Date().getTime();
				// console.log(
				// 	`%cprojectId = ${projectId}, userId = ${userId}, userIsActive = ${userIsActive}, timeNowInMS = ${timeNowInMS}`,
				// 	'background: #BBFFBB'
				// );
				if (this.presenceNeedsToBeUpdated(projectId, userId, userIsActive, timeNowInMS)) {
					analyticsEventsService.pokeProjectPresence(projectId);
					// Poke project claims
					pokeProjectPresence(projectId, userId, fancyDatabase).then(() => {
						// Save the information about this recorded presence, including the time:
						this.currentPresencesProjectId = projectId;
						this.currentPresencesUserId = userId;
						this.currentPresencesTimeInMS = timeNowInMS;
					});
				}
			});
	}

	private startUserActivityListeners() {
		document.addEventListener('visibilitychange', () => {
			this.handleCurrentDocumentVisibility();
		});

		window.onclick = () => {
			this.userIsActive(true);
		};

		window.touchstart = () => {
			this.userIsActive(true);
		};

		this.handleCurrentDocumentVisibility();
	}

	private handleCurrentDocumentVisibility() {
		this.userIsActive(document.visibilityState === 'visible');
	}

	private userIsActive(active: boolean) {
		this.userIsActiveSubject.next(active);
	}

	private presenceNeedsToBeUpdated(
		projectId: string,
		userId: string,
		userIsActive: boolean,
		timeNowInMS: number
	): boolean {
		// If the user is inactive, don't update presence:
		if (!userIsActive) {
			return false;
		}
		// If the current project has become initialized or it's changed, update presence (as long as there's a user-id):
		if (!this.currentPresencesProjectId || (!!projectId && this.currentPresencesProjectId !== projectId)) {
			return !!userId;
		}
		// If the current user has become initialized or it's changed, update presence:
		if (!this.currentPresencesUserId || (!!userId && this.currentPresencesUserId !== userId)) {
			return true;
		}
		// Update presence if it has been MAX_POKE_PERIOD_MINUTES or more since the last update:
		if (!this.currentPresencesTimeInMS) {
			return true;
		}
		const msSinceLastActiveEvent = timeNowInMS - this.currentPresencesTimeInMS;
		return msSinceLastActiveEvent > this.MAX_POKE_PERIOD_MINUTES * 60 * 1000;
	}

	/*
	 * Returns the time elapsed between two dates in milliseconds
	 */
	private timeElapsedMilliseconds(date1: Date, date2: Date): number {
		if (!date1 || !date2) return 0;
		return Math.abs(date2.getTime() - date1.getTime());
	}
}
